/**
 * Adapter between the odl_app calendar occurrence API and the MUI X Scheduler.
 *
 * The scheduler is fed pre-expanded, non-recurring events — one per server
 * occurrence — because recurrence is expanded on the backend and the client
 * must never hand the scheduler an RRULE (that would activate the Premium
 * recurring-scope dialog). This module owns both directions of that boundary:
 *
 *  - occurrence row -> SchedulerEvent: wall-time strings (rendered in the site
 *    timezone, no shift), per-relay hex color via a className (the scheduler's
 *    built-in palette is an 11-name set, so arbitrary relay colors ride a class
 *    + scoped CSS variables), and readOnly for sun-relative occurrences whose
 *    on/off time is computed rather than clock-fixed.
 *  - onEventsChange -> edit intent: the scheduler reports the whole event array
 *    after a drag/resize, so we diff it by synthetic occurrence id to recover
 *    which occurrence moved and to what.
 *
 * Resources are one row per relay that drives at least one load, labelled by that
 * relay's load name(s) (e.g. "Face 1, Face 2"), since that is how operators read
 * the schedule. Each occurrence attaches to its relay's single resource row (no
 * duplication); occurrences on a relay without loads render unassigned. Colors
 * stay relay-keyed.
 *
 * Contains:
 * - occurrenceToEvent / occurrencesToEvents — API row -> SchedulerEvent(s)
 * - buildResources / buildResourceIdByRelayId / relayLoadLabel — resource rows + lookup + label
 * - relayColorClassName / relayColorCssVars — per-relay chip color plumbing
 * - eventIdClassName / parseEventIdFromClassList — click-interception id bridge
 * - findEventIdFromTarget — walk a clicked DOM node up to its id-bearing ancestor
 * - diffEventChange — onEventsChange array -> {eventId, occurrenceDate, newStart, newEnd}
 * - MONTH_CELL_MIN_HEIGHT — shared month-view cell height for both site calendars
 *
 * Returns plain JS — no JSX, no redux.
 */

import { getRelayBaseColor } from '@sophocles/views/sites/utils/calendarColors';

export const RELAY_COLOR_CLASS_PREFIX = 'odl-cal-relay-';
export const EVENT_ID_CLASS_PREFIX = 'odl-cal-evt-';
const RELAY_RESOURCE_PREFIX = 'relay-';

// Month cells hold four event chips before the "+N more" collapse: the
// scheduler derives its per-day chip budget from measured cell height
// (cell padding 5 + day-number header 22 + 4 × (chip 18 + gap 4) = 115,
// plus slack). Rows past the container scroll within the month view.
export const MONTH_CELL_MIN_HEIGHT = 120;

// The scheduler's fixed palette names, mapped by relay index only to give the
// built-in resource-panel swatches distinct (approximate) colors. The event
// chips themselves are colored by exact hex via relayColorCssVars, not these.
const PALETTE_NAMES = ['blue', 'green', 'orange', 'purple', 'pink', 'teal', 'red', 'grey', 'amber', 'indigo'];

const WALL_TIME_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?/;

/**
 * Reduce a datetime string to a naive site-local wall-time (`YYYY-MM-DDTHH:MM:SS`),
 * dropping any `Z`/`±HH:MM` zone designator and fractional seconds. The backend
 * emits occurrences already expressed in the site timezone, so the wall-clock
 * portion is what the scheduler should render when told the site timezone.
 */
export function toWallTime(iso) {
  const match = String(iso).match(WALL_TIME_RE);
  if (!match) {
    return String(iso);
  }
  const seconds = match[3] ?? '00';
  return `${match[1]}T${match[2]}:${seconds}`;
}

/** A sun-relative occurrence cannot be dragged: its clock time is computed. */
function isSunTimed(occurrence) {
  const onType = occurrence.on?.type;
  const offType = occurrence.off?.type;
  return (onType != null && onType !== 'time') || (offType != null && offType !== 'time');
}

/** Stable per-relay class carrying that relay's color CSS variables. */
export function relayColorClassName(relayNumber) {
  return `${RELAY_COLOR_CLASS_PREFIX}${relayNumber}`;
}

/**
 * Class token that encodes an occurrence id onto its event chip. The scheduler
 * exposes no click callback and no id attribute on the chip DOM, so this class
 * (a documented event-model passthrough, unlike the internal markup) is how the
 * capture-phase click handler recovers which occurrence was clicked.
 */
export function eventIdClassName(id) {
  return `${EVENT_ID_CLASS_PREFIX}${id}`;
}

/**
 * @param {DOMTokenList|string[]|undefined} classList - an element's classList.
 * @returns {?string} The occurrence id encoded by eventIdClassName, or null.
 */
export function parseEventIdFromClassList(classList) {
  if (!classList) {
    return null;
  }
  for (const token of classList) {
    if (token.startsWith(EVENT_ID_CLASS_PREFIX)) {
      return token.slice(EVENT_ID_CLASS_PREFIX.length);
    }
  }
  return null;
}

/**
 * Walk up from a clicked DOM node to the first ancestor tagged with an event
 * id (the scheduler renders event chips with nested markup, so the click
 * target itself rarely carries the class).
 *
 * @param {?EventTarget} target - The DOM node a click/keydown event fired on.
 * @returns {?string} The event id encoded by eventIdClassName, or null.
 */
export function findEventIdFromTarget(target) {
  let node = target;
  while (node) {
    const id = parseEventIdFromClassList(node.classList);
    if (id) {
      return id;
    }
    node = node.parentElement;
  }
  return null;
}

function lighten(hex, amount) {
  const value = hex.replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const mix = channel => Math.min(255, Math.floor(channel + (255 - channel) * amount));
  const toHex = channel => mix(channel).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * The scheduler event-chip CSS variables for a relay's hex color. Applied via a
 * scoped selector (`.<calendarRoot> .<relayClass>`) so its specificity beats the
 * library's `[data-palette]` variant without `!important`.
 */
export function relayColorCssVars(relayNumber) {
  const hex = getRelayBaseColor(relayNumber);
  return {
    '--event-main': hex,
    '--event-surface-bold': hex,
    '--event-surface-bold-hover': hex,
    '--event-on-surface-bold': '#ffffff',
    '--event-surface-subtle': lighten(hex, 0.82),
    '--event-surface-subtle-hover': lighten(hex, 0.72),
    '--event-surface-accent': hex,
    '--event-on-surface-subtle-primary': hex,
    '--event-on-surface-subtle-secondary': lighten(hex, 0.28),
  };
}

/** Scheduler resource id for one relay's row. */
function relayResourceId(relayId) {
  return `${RELAY_RESOURCE_PREFIX}${relayId}`;
}

/**
 * A relay's load names joined for its resource-row label (matches the dialog).
 *
 * @param {object} relay - relay row ({relay_number, loads?}).
 * @param {Function} t - i18next translator (the no-loads fallback label).
 * @returns {string} the joined load names, or the relay-number fallback.
 */
export function relayLoadLabel(relay, t) {
  const loadNames = (relay.loads ?? []).map(load => load.name || load.display_name).filter(Boolean);
  return loadNames.length > 0
    ? loadNames.join(' / ')
    : t('sites.relayNumber', 'Relay {{number}}', { number: relay.relay_number });
}

/** Relays that drive at least one load — the only ones shown as resource rows. */
function relaysWithLoads(relays) {
  return (relays ?? []).filter(relay => (relay.loads?.length ?? 0) > 0);
}

/**
 * @param {object} occurrence - A row from the calendar `occurrences/` endpoint.
 * @param {{timezone?: string, resourceId?: ?string}} [options] - Site IANA
 *   timezone and the relay resource row this event attaches to.
 * @returns {object} A SchedulerEvent (non-recurring, wall-time strings).
 */
export function occurrenceToEvent(occurrence, { timezone, resourceId = null } = {}) {
  const relay = occurrence.relay ?? {};
  const classes = [eventIdClassName(occurrence.id)];
  if (relay.relay_number != null) {
    classes.push(relayColorClassName(relay.relay_number));
  }
  return {
    id: occurrence.id,
    title: occurrence.name ?? '',
    start: toWallTime(occurrence.start),
    end: toWallTime(occurrence.end),
    timezone,
    resource: resourceId,
    allDay: Boolean(occurrence.always_on),
    readOnly: isSunTimed(occurrence),
    className: classes.join(' '),
  };
}

/**
 * @param {object[]} occurrences - Rows from the `occurrences/` endpoint.
 * @param {{timezone?: string, resourceIdByRelayId?: Map<any, string>}} [options]
 *   - Site IANA timezone and the relay-id -> resource-id lookup. Each occurrence
 *   attaches to its relay's resource row (unassigned when the relay has none).
 * @returns {object[]} SchedulerEvents.
 */
export function occurrencesToEvents(occurrences, { timezone, resourceIdByRelayId } = {}) {
  return (occurrences ?? []).map(occurrence => {
    const resourceId = resourceIdByRelayId?.get(occurrence.relay?.id) ?? null;
    return occurrenceToEvent(occurrence, { timezone, resourceId });
  });
}

/**
 * @param {object[]} relays - Site relays (plain JS: {id, relay_number, loads?}).
 * @param {Function} t - i18next translator (the no-loads fallback label).
 * @returns {object[]} One SchedulerResource per relay that has loads, labelled by
 *   the relay's load name(s) and colored per relay for the panel swatch.
 */
export function buildResources(relays, t) {
  return relaysWithLoads(relays).map(relay => ({
    id: relayResourceId(relay.id),
    title: relayLoadLabel(relay, t),
    eventColor: PALETTE_NAMES[(relay.relay_number - 1) % PALETTE_NAMES.length],
  }));
}

/**
 * @param {object[]} relays - Site relays (plain JS with optional `loads`).
 * @returns {Map<any, string>} relay id -> its resource id, for relays with loads.
 *   Passed to occurrencesToEvents to place events on the right resource row.
 */
export function buildResourceIdByRelayId(relays) {
  return new Map(relaysWithLoads(relays).map(relay => [relay.id, relayResourceId(relay.id)]));
}

/**
 * Recover the single occurrence a drag/resize changed by diffing the event
 * array the scheduler reports against the array we last supplied.
 *
 * @param {object[]} prevEvents - Events last passed to the scheduler.
 * @param {object[]} nextEvents - The array from onEventsChange.
 * @returns {?{eventId: string, occurrenceDate: ?string, newStart: string, newEnd: string}}
 *   The edit intent, or `null` when nothing (or ambiguously more than one thing)
 *   changed. `occurrenceDate` is the edited occurrence's original date (from its
 *   synthetic id `"<eventId>:<YYYY-MM-DD>"`), independent of where it moved to.
 */
export function diffEventChange(prevEvents, nextEvents) {
  const prevById = new Map((prevEvents ?? []).map(event => [event.id, event]));

  let changed = null;
  for (const next of nextEvents ?? []) {
    const prev = prevById.get(next.id);
    if (!prev) {
      continue;
    }
    const moved = toWallTime(prev.start) !== toWallTime(next.start) || toWallTime(prev.end) !== toWallTime(next.end);
    if (!moved) {
      continue;
    }
    if (changed) {
      return null;
    }
    changed = next;
  }

  if (!changed) {
    return null;
  }

  const id = String(changed.id);
  const separator = id.indexOf(':');
  return {
    eventId: separator === -1 ? id : id.slice(0, separator),
    occurrenceDate: separator === -1 ? null : id.slice(separator + 1),
    newStart: toWallTime(changed.start),
    newEnd: toWallTime(changed.end),
  };
}
